<template>
    <div class="scanner-container">
        <div v-if="error" class="scanner-error pa-3">{{ error }}</div>
        <div v-show="!isLoading && !error" class="video-wrap">
            <video poster="data:image/gif,AAAA" ref="scanner" playsinline muted></video>
            <div class="overlay-element"></div>
            <div class="laser"></div>
            <v-btn v-if="torchSupported" class="torch" small fab dark :color="torchOn ? 'warning' : 'secondary'" @click="toggleTorch">
                <v-icon dark>{{ torchOn ? 'mdi-flashlight-off' : 'mdi-flashlight' }}</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat } from "@zxing/library";

export default {
    name: "barcode-scanner",

    data() {
        return {
            isLoading: true,
            error: "",
            torchSupported: false,
            torchOn: false,
            codeReader: null,
        };
    },

    mounted() {
        if (!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
            this.error = "Kamera wird von diesem Gerät/Browser nicht unterstützt.";
            return;
        }
        document.addEventListener("visibilitychange", this.onVisibility);
        this.start();
    },

    beforeDestroy() {
        document.removeEventListener("visibilitychange", this.onVisibility);
        this.stop();
    },

    methods: {
        onVisibility() {
            if (document.visibilityState === "hidden") {
                this.stop();
            } else if (!this.codeReader) {
                this.start();
            }
        },

        stop() {
            if (this.codeReader) {
                this.codeReader.reset();
                this.codeReader = null;
            }
            this.isLoading = true;
            this.torchOn = false;
            this.torchSupported = false;
        },

        async pickDeviceId() {
            // Gewählte Kamera merken: spart das zusätzliche Öffnen/Schließen der Kamera bei jedem Start
            try {
                const cached = localStorage.getItem("camera-device");
                if (cached) return cached;
            } catch (e) { /* ignorieren */ }
            // Erst Berechtigung holen, damit enumerateDevices Labels liefert
            const tmp = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            tmp.getTracks().forEach(t => t.stop());
            const devices = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "videoinput");
            const back = devices.filter(d => /back|rear|environment|rück/i.test(d.label));
            const pool = back.length ? back : devices;
            // Ultraweitwinkel/Tele meiden, haben oft keinen Nahfokus
            const main = pool.find(d => !/wide|ultra|tele|depth|macro/i.test(d.label)) || pool[0];
            if (main) {
                try { localStorage.setItem("camera-device", main.deviceId); } catch (e) { /* ignorieren */ }
            }
            return main ? main.deviceId : undefined;
        },

        async start() {
            this.error = "";
            this.isLoading = true;
            try {
                const hints = new Map();
                hints.set(DecodeHintType.TRY_HARDER, true);
                hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                    BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.EAN_13,
                    BarcodeFormat.EAN_8, BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
                    BarcodeFormat.DATA_MATRIX, BarcodeFormat.CODABAR, BarcodeFormat.UPC_A,
                ]);
                const reader = new BrowserMultiFormatReader(hints, 200);
                this.codeReader = reader;

                const deviceId = await this.pickDeviceId();
                if (this.codeReader !== reader) return; // inzwischen gestoppt

                const constraints = {
                    video: {
                        deviceId: deviceId ? { exact: deviceId } : undefined,
                        facingMode: deviceId ? undefined : "environment",
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                };
                const video = this.$refs.scanner;
                video.oncanplay = () => {
                    this.isLoading = false;
                    this.$emit("loaded");
                    this.setupTrack(video);
                };
                await reader.decodeFromConstraints(constraints, video, (result) => {
                    if (result) this.$emit("decode", result.text);
                });
            } catch (e) {
                this.codeReader = null;
                if (e && e.name === "OverconstrainedError" && !this.retried) {
                    // gemerkte Kamera existiert nicht mehr -> neu suchen
                    this.retried = true;
                    try { localStorage.removeItem("camera-device"); } catch (err) { /* ignorieren */ }
                    return this.start();
                }
                this.error = this.errorText(e);
                this.$emit("error", e);
            }
        },

        setupTrack(video) {
            const stream = video.srcObject;
            const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
            if (!track || !track.getCapabilities) return;
            const caps = track.getCapabilities();
            if (caps.focusMode && caps.focusMode.includes("continuous")) {
                track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
            }
            this.torchSupported = !!caps.torch;
            if (this.torchSupported && this.initialTorch()) this.setTorch(track, true);
        },

        initialTorch() {
            try {
                const s = JSON.parse(localStorage.getItem("settings") || "{}");
                const mode = s.taschenlampe || "last";
                if (mode === "on") return true;
                if (mode === "last") return localStorage.getItem("torch-last") === "1";
            } catch (e) { /* localStorage nicht verfügbar */ }
            return false;
        },

        setTorch(track, on) {
            return track.applyConstraints({ advanced: [{ torch: on }] })
                .then(() => {
                    this.torchOn = on;
                    try { localStorage.setItem("torch-last", on ? "1" : "0"); } catch (e) { /* ignorieren */ }
                })
                .catch(() => {});
        },

        toggleTorch() {
            const video = this.$refs.scanner;
            const track = video && video.srcObject && video.srcObject.getVideoTracks()[0];
            if (!track) return;
            this.setTorch(track, !this.torchOn);
        },

        errorText(e) {
            switch (e && e.name) {
                case "NotAllowedError":
                case "SecurityError":
                    return "Kamerazugriff verweigert. Bitte in den Browser-/Geräteeinstellungen die Kamera für diese App erlauben.";
                case "NotFoundError":
                case "OverconstrainedError":
                    return "Keine geeignete Kamera gefunden.";
                case "NotReadableError":
                    return "Kamera wird von einer anderen App verwendet oder konnte nicht gestartet werden.";
                default:
                    return "Kamera konnte nicht gestartet werden.";
            }
        },
    },
};
</script>

<style scoped>
video {
    display: block;
    width: 100%;
    height: 100%;
    /* Bild füllt den begrenzten Bereich, damit es nicht aus dem Browserfenster ragt */
    object-fit: cover;
}
.video-wrap {
    position: relative;
    overflow: hidden;
    height: 40vh;
    max-height: 320px;
}
.scanner-container {
    position: relative;
}
.scanner-error {
    color: #F44336;
}
.torch {
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 3;
}

.overlay-element {
    position: absolute;
    top: 0;
    width: 100%;
    height: 99%;
    background: rgba(30, 30, 30, 0.5);
    -webkit-clip-path: polygon(0% 0%, 0% 100%, 20% 100%, 20% 20%, 80% 20%, 80% 80%, 20% 80%, 20% 100%, 100% 100%, 100% 0%);
    clip-path: polygon(0% 0%, 0% 100%, 20% 100%, 20% 20%, 80% 20%, 80% 80%, 20% 80%, 20% 100%, 100% 100%, 100% 0%);
}

.laser {
    width: 60%;
    margin-left: 20%;
    background-color: tomato;
    height: 1px;
    position: absolute;
    top: 40%;
    z-index: 2;
    box-shadow: 0 0 4px red;
    -webkit-animation: scanning 2s infinite;
    animation: scanning 2s infinite;
}
@-webkit-keyframes scanning {
    50% { -webkit-transform: translateY(75px); transform: translateY(75px); }
}
@keyframes scanning {
    50% { -webkit-transform: translateY(75px); transform: translateY(75px); }
}
</style>
